import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import useStyles from "./ButtonChoice.styles";
import { RadioIcon } from "./RadioIcon";
import { CheckBoxIcon } from "./CheckBoxIcon";
import { VariableModel } from "../../models/VariableModel";
import useQuestion from "../../hooks/useQuestion";
import {
    getAriaLabelledByFromId,
    useAriaLabelledByForQuestionOpenEndChoiceBox,
} from "../../utils/WCAGHelper";
import { useKeyboardNavigation } from "../../hooks/useKeyboardNavigation";
import { useMouseHover } from "../../hooks/useMouseHover";
import { wasLastInteractionKeyboard } from "../../utils/inputModality";
import { ButtonChoiceNavigation } from "../controls/KeyboardNavigation/ButtonChoiceNavigation";
import { QuestionType } from "../../models/enums/QuestionType";
import { shouldUseModernTheme } from "../../theme/navigationButtonThemeColors";

interface Props {
    id?: string;
    choiceElementId?: string;
    type: "checkbox" | "radio";
    label?: string;
    className?: string;
    disabled?: boolean;
    selected?: boolean;
    hasOpenEnd?: boolean;
    openEndSize?: string;
    horizontal?: boolean;
    onSelect?(): void;
    onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
    renderChoice?(): any;
    renderAfter?(): any;
    carrouselChoiceBackgound?: string;
    variable?: VariableModel;
    /** When set, overrides default aria-label / open-end aria-labelledby (e.g. carousel scale anchors). */
    ariaLabelledBy?: string;
    isCarrouselScaleActive?: boolean;
    isCarrouselScaleHorizontalRow?: boolean;
    /** Modern collapsible grid panel: no inset border shadow or stack margin. */
    flatModernSurface?: boolean;
}

export function ButtonChoice(props: Props) {
    const {
        id,
        choiceElementId,
        disabled,
        label,
        type,
        className,
        hasOpenEnd,
        openEndSize,
        horizontal,
        selected,
        onSelect,
        renderChoice,
        renderAfter,
        carrouselChoiceBackgound,
        variable,
        ariaLabelledBy,
        isCarrouselScaleActive,
        isCarrouselScaleHorizontalRow,
        flatModernSurface,
    } = props;

    const question = useQuestion();
    const [hover, pointerHandlers] = useMouseHover();
    const [active, setActive] = React.useState(false);
    const [keyboardFocused, setKeyboardFocused] = React.useState(false);
    const useAriaLabelledBy = useAriaLabelledByForQuestionOpenEndChoiceBox(question);

    const theme = useTheme() as any;
    const useModernTheme = shouldUseModernTheme(theme);
    const classes = useStyles({
        theme,
        selected,
        hasOpenEnd,
        openEndSize,
        horizontal,
        carrouselChoiceBackgound,
        type,
        isCarrouselScaleActive,
        isCarrouselScaleHorizontalRow,
        flatModernSurface,
    });

    const ChoiceIcon = type === "radio" ? RadioIcon : CheckBoxIcon;

    const { elementReference, onKeyDown } = useKeyboardNavigation(
        id,
        variable,
        question,
        ButtonChoiceNavigation,
        onSelect,
    );

    return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
            id={id}
            role={type}
            ref={elementReference}
            aria-checked={selected}
            aria-disabled={disabled}
            {...(ariaLabelledBy
                ? { "aria-labelledby": ariaLabelledBy }
                : useAriaLabelledBy
                  ? { "aria-labelledby": getAriaLabelledByFromId(question, choiceElementId) }
                  : { "aria-label": label })}
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={disabled ? -1 : 0}
            onFocus={(e) => {
                setActive(true);
                if (useModernTheme && e.target === e.currentTarget) {
                    setKeyboardFocused(wasLastInteractionKeyboard());
                }
            }}
            onBlur={() => {
                setActive(false);
                setKeyboardFocused(false);
            }}
            {...pointerHandlers}
            onKeyDown={onKeyDown}
            onMouseDown={(e) => {
                const div = e.target as HTMLDivElement;
                const activeElement = document.activeElement as HTMLElement;
                if (!div.contains(activeElement)) activeElement.blur();
                e.preventDefault();
            }}
            onClick={() => {
                onSelect();
                if (question.type != QuestionType.Carrousel || type != "radio") {
                    document.getElementById(id).focus();
                }
            }}
            className={classnames(
                classes.choice,
                ...([className] || []),
                disabled && classes.choiceDisabled,
                hover && classes.choiceHover,
                selected && classes.choiceSelected,
                useModernTheme && keyboardFocused && classes.choiceKeyboardFocus,
                !useModernTheme && active && classes.choiceActive,
            )}
        >
            <div className={classes.choiceContainer}>
                {renderChoice ? (
                    renderChoice()
                ) : (
                    <>
                        <ChoiceIcon checked={selected} disabled={disabled} hover={hover} />
                        {theme.DisplayChoiceIcon !== "none" && (
                            <span className={classes.spacer} aria-hidden="true" />
                        )}
                        <span
                            className={classes.choiceText}
                            id={id}
                            dangerouslySetInnerHTML={{ __html: label }}
                        />
                    </>
                )}
            </div>
            {renderAfter && renderAfter()}
        </div>
    );
}
